// 这个文件只做一件事：
// 如果 URL 路径的第一段是语言标识，就把它去掉，转成 H5 当前已有的路由格式。
//
// 例子：
// /zh_cn/quotes -> /quotes
// /en_us/home -> /home
// /ja_jp -> /

/**
 * 判断一个路径是否带了语言前缀，并在需要时返回“去掉语言前缀后的新路径”。
 *
 * 这个函数只做“字符串层面的路径分析”，不会操作浏览器地址，也不会写 localStorage。
 *
 * 入参示例：
 * pathname: "/zh_cn/quotes"
 * supportedLocales: ["zh_cn", "en_us", "zh_tw", "ja_jp"]
 *
 * 返回示例：
 * {
 *   changed: true,       // 是否识别到了语言前缀
 *   locale: "zh_cn",     // 识别到的语言
 *   pathname: "/quotes", // 去掉语言前缀后的真实路径
 * }
 */
function normalizeLocalePrefixedPath (pathname = '/', supportedLocales = []) {
  // 兜底，保证 pathname 至少是 "/"
  const currentPathname = pathname || '/'

  // 拆路径段：
  // "/zh_cn/quotes" => ["zh_cn", "quotes"]
  // "/" => []
  const segments = currentPathname.split('/').filter(Boolean)

  // 根路径没有路径段，不需要处理
  if (segments.length === 0) {
    return {
      changed: false,
      locale: null,
      pathname: '/',
    }
  }

  // 第一段是候选语言，后面剩下的是真实业务路径
  const [locale, ...restSegments] = segments

  // 如果第一段不是支持的语言，就保持原样
  if (!supportedLocales.includes(locale)) {
    return {
      changed: false,
      locale: null,
      pathname: currentPathname,
    }
  }

  // 如果第一段就是语言，就把它去掉
  // ["zh_cn", "quotes"] => "/quotes"
  // ["en_us"] => "/"
  return {
    changed: true,
    locale,
    pathname: restSegments.length ? `/${restSegments.join('/')}` : '/',
  }
}

/**
 * 在浏览器环境中执行 URL 兼容处理。
 *
 * 它会调用 normalizeLocalePrefixedPath 先判断当前地址是否带语言前缀，
 * 如果带了，就做 3 件事：
 * 1. 把路径中的语言前缀去掉
 * 2. 把语言写入 localStorage.locale
 * 3. 用 history.replaceState 原地替换浏览器地址
 *
 * 这个函数是真正和浏览器交互的入口函数，
 * 用来保证 Vue Router 启动前拿到的是兼容后的路径。
 */
function applyLocalePathCompatibility ({ supportedLocales = [], win = window } = {}) {
  // 非浏览器环境直接跳过，避免没有 window 时报错
  if (!win?.location) {
    return {
      changed: false,
      locale: null,
      pathname: '/',
      fullPath: '/',
    }
  }

  // 取浏览器当前地址的三部分
  // pathname: /zh_cn/quotes
  // search: ?foo=bar
  // hash: #section
  const { pathname, search = '', hash = '' } = win.location

  // 先只处理路径部分，判断是否需要去掉语言前缀
  const normalized = normalizeLocalePrefixedPath(pathname, supportedLocales)

  // 把 query 和 hash 重新拼回去，避免参数丢失
  const fullPath = `${normalized.pathname}${search}${hash}`

  // 只有检测到语言前缀时，才执行兼容逻辑
  if (normalized.changed) {
    try {
      // 把路径里的语言同步到 localStorage.locale
      // 这样 URL 虽然改成了 /quotes，但页面语言仍能保持 zh_cn / en_us
      if (normalized.locale) {
        win.localStorage?.setItem('locale', normalized.locale)
      }
    } catch (error) {
      // localStorage 写入失败不影响主流程，只做日志提示
      console.warn('set locale from path failed:', error)
    }

    // 原地替换地址：
    // 1. 不刷新页面
    // 2. 不新增浏览器历史记录
    // 3. 让后面的 Vue Router 直接拿到兼容后的路径
    win.history?.replaceState?.(win.history.state, '', fullPath)
  }

  // 返回处理结果，方便测试和调试
  return {
    ...normalized,
    fullPath,
  }
}

export {
  normalizeLocalePrefixedPath,
  applyLocalePathCompatibility,
}
